import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const ShopContext = createContext(null);

const read = (k, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(k)) ?? fallback;
  } catch {
    return fallback;
  }
};

export function ShopProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [cart, setCart] = useState(() => read("vv_cart", []));
  const [wishlist, setWishlist] = useState(() => read("vv_wishlist", []));
  const [cartOpen, setCartOpen] = useState(false);
  const [announcement, setAnnouncement] = useState("INTRODUCING THE NEW SEASON — EXPLORE COLLECTION 01");
  const [offers, setOffers] = useState([]);
  const [payConfig, setPayConfig] = useState({ cashfree: false, env: "sandbox", demo: true });

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth, skip /auth/me — AuthCallback exchanges the session_id first
    if (window.location.hash?.includes("session_id=")) {
      setAuthChecked(true);
      return;
    }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null))
      .finally(() => setAuthChecked(true));
    api.get("/settings/public").then((r) => {
      setAnnouncement(r.data.announcement);
      setOffers(r.data.coupons || []);
      setPayConfig({ cashfree: r.data.payments?.cashfree || false, env: r.data.payments?.env || "sandbox",
                     demo: r.data.demo_checkout !== false });
    }).catch(() => {});
  }, []);

  useEffect(() => localStorage.setItem("vv_cart", JSON.stringify(cart)), [cart]);
  useEffect(() => localStorage.setItem("vv_wishlist", JSON.stringify(wishlist)), [wishlist]);

  const addToCart = useCallback((product, size, color, qty = 1) => {
    const key = `${product.id || product._id}-${size}-${color}`;
    setCart((prev) => {
      const found = prev.find((i) => i.key === key);
      if (found) return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      return [...prev, {
        key, product_id: product.id, slug: product.slug, name: product.name,
        price: product.price, image: product.images?.[0] || "", size, color, qty,
      }];
    });
    toast.success("Added to bag", { description: `${product.name} · ${size}` });
  }, []);

  const updateQty = useCallback((key, delta) => {
    setCart((prev) => prev
      .map((i) => (i.key === key ? { ...i, qty: i.qty + delta } : i))
      .filter((i) => i.qty > 0));
  }, []);

  const removeItem = useCallback((key) => setCart((prev) => prev.filter((i) => i.key !== key)), []);
  const clearCart = useCallback(() => setCart([]), []);

  const toggleWishlist = useCallback((product) => {
    setWishlist((prev) => {
      const exists = prev.find((p) => p.id === product.id);
      if (exists) {
        toast("Removed from wishlist");
        return prev.filter((p) => p.id !== product.id);
      }
      toast.success("Saved to wishlist", { description: product.name });
      return [...prev, { id: product.id, slug: product.slug, name: product.name, price: product.price, image: product.images?.[0] || "" }];
    });
  }, []);

  const subtotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.qty, 0), [cart]);
  const count = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);

  const value = {
    user, setUser, authChecked,
    cart, addToCart, updateQty, removeItem, clearCart, subtotal, count,
    wishlist, toggleWishlist,
    cartOpen, setCartOpen,
    announcement, setAnnouncement,
    offers,
    payConfig,
  };
  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
}

export function useShop() {
  return useContext(ShopContext);
}
